import {
    PeriodComparison,
    RatioDailyAggregatedItem,
    RatioEvents,
    RatioEventItems,
    TransitionEvents,
    TransitionEventItems,
} from '@/types/AnalysisResultType';

/**
 * 前期比較データを計算
 * @param currentValue 今期値
 * @param previousValue 前期値
 * @returns PeriodComparison
 */
export function calculatePeriodComparison(
    currentValue: number,
    previousValue: number,
): PeriodComparison {
    const changeAmount = currentValue - previousValue;
    let changePercentage = 0;

    if (previousValue !== 0) {
        // パーセンテージ算出（小数点第1位まで）
        changePercentage = Math.round((changeAmount / previousValue) * 100 * 10) / 10;
    } else if (currentValue > 0) {
        // 前期が0で今期が0より大きい場合は100%増加とする
        changePercentage = 100;
    }

    return {
        previousValue,
        currentValue,
        changePercentage,
        changeAmount,
        isIncrease: changeAmount >= 0,
    };
}

const mapDailyAggregatedData = (dailyData: any): RatioDailyAggregatedItem[] => {
    if (!Array.isArray(dailyData)) {
        return [];
    }

    return dailyData.map((item: any) => ({
        date: item?.date ?? '',
        totalTime: Number(item?.totalTime ?? 0),
        eventItems: Array.isArray(item?.eventItems)
            ? item.eventItems.map((dailyItem: any) => ({
                  title: dailyItem?.title ?? '',
                  totalTime: Number(dailyItem?.totalTime ?? 0),
                  percentage: Number(dailyItem?.percentage ?? 0),
              }))
            : [],
    }));
};

/**
 * 比率データの前期比較を計算
 * @param currentData 今期データ
 * @param previousData 前期データ
 * @returns 前期比較データ付きの今期データ
 */
export function calculateRatioComparison(currentData: any, previousData: any): RatioEvents {
    const result: RatioEvents = {
        ratioEventItems: [],
        totalTime: currentData.totalTime || 0,
        averageTime: currentData.averageTime || 0,
        totalCount: currentData.totalCount || 0,
        totalTimeComparison: calculatePeriodComparison(
            currentData.totalTime || 0,
            previousData?.previousTotalTime || 0,
        ),
        averageTimeComparison: calculatePeriodComparison(
            currentData.averageTime || 0,
            previousData?.previousAverageTime || 0,
        ),
        totalCountComparison: calculatePeriodComparison(
            currentData.totalCount || 0,
            previousData?.previousTotalCount || 0,
        ),
        dailyAggregatedData: mapDailyAggregatedData(currentData?.dailyAggregatedData),
    };

    // 前期データをマップ化（タイトルとイベント情報）
    const previousEventMap = new Map<string, any>();
    if (previousData?.previousAggregatedDataByEventList) {
        for (const event of previousData.previousAggregatedDataByEventList) {
            previousEventMap.set(event.title, event);
        }
    }

    // 今期のイベントごとに前期比を計算
    result.ratioEventItems = (currentData.aggregatedDataByEventList || []).map(
        (currentEvent: any): RatioEventItems => {
            const previousEvent = previousEventMap.get(currentEvent.title);

            return {
                title: currentEvent.title,
                totalTime: currentEvent.totalTime,
                percentage: currentEvent.percentage,
                count: currentEvent.count,
                averageTimePerCount: currentEvent.averageTimePerCount,
                averageTimePerDay: currentEvent.averageTimePerDay,
                totalTimeComparison: calculatePeriodComparison(
                    currentEvent.totalTime,
                    previousEvent?.totalTime || 0,
                ),
                countComparison: calculatePeriodComparison(
                    currentEvent.count,
                    previousEvent?.count || 0,
                ),
            };
        },
    );

    return result;
}

/**
 * 推移データの前期比較を計算
 * @param currentData 今期データ
 * @param previousData 前期データ（詳細）
 * @returns 前期比較データ付きの今期データ
 */
export function calculateTransitionComparison(
    currentData: any,
    previousData?: any[],
): TransitionEvents {
    const result: TransitionEvents = {
        transitionEventItems: [],
        totalTime: currentData.totalTime || 0,
        activeDays: currentData.activeDays || 0,
        periodDays: currentData.periodDays || 0,
        activeDaysRatio: currentData.activeDaysRatio || 0,
        averageTimePerDay: currentData.averageTimePerDay || 0,
    };

    // 前期データがある場合は前期比を計算
    if (previousData && previousData.length > 0) {
        // 前期データから集計値を計算
        // 合計時間
        const previousTotalTime = previousData.reduce((sum, item) => sum + (item.time || 0), 0);
        // 活動日数
        const previousActiveDays = previousData.filter((item) => (item.time || 0) > 0).length;
        // 平均時間(1日あたり)
        const previousAverageTimePerDay =
            previousActiveDays > 0 ? previousTotalTime / result.periodDays : 0;

        // 合計時間
        result.totalTimeComparison = calculatePeriodComparison(result.totalTime, previousTotalTime);
        // 活動日数
        result.activeDaysComparison = calculatePeriodComparison(
            result.activeDays,
            previousActiveDays,
        );
        // 平均時間(1日あたり)
        result.averageTimePerDayComparison = calculatePeriodComparison(
            result.averageTimePerDay,
            previousAverageTimePerDay,
        );

        // 前期データをマップ化
        const previousDataMap = new Map<string, any>();
        for (const item of previousData) {
            previousDataMap.set(item.date, item);
        }

        // 今期の日別データごとに前期比を計算
        result.transitionEventItems = (currentData.dateAndTimeList || []).map(
            (currentItem: any): TransitionEventItems => {
                const previousItem = previousDataMap.get(currentItem.date);

                return {
                    date: currentItem.date,
                    time: currentItem.time,
                    timeComparison: calculatePeriodComparison(
                        currentItem.time,
                        previousItem?.time || 0,
                    ),
                };
            },
        );
    } else {
        // 前期データがない場合はそのまま
        result.transitionEventItems = (currentData.dateAndTimeList || []).map(
            (item: any): TransitionEventItems => ({
                date: item.date,
                time: item.time,
            }),
        );
    }

    // 前期の詳細データもセット
    if (previousData && previousData.length > 0) {
        result.previousDateAndTimeList = previousData;
    }

    return result;
}

/**
 * 比率データを棒グラフ用データに変換（TransitionEventItems形式）
 * @param ratioData 比率データ
 * @returns 棒グラフ用データ（TransitionEventItems形式）
 */
export function convertRatioToBarChartData(ratioData: RatioEvents): TransitionEventItems[] {
    return ratioData.ratioEventItems.map((event) => ({
        date: event.title, // イベント名を日付の代わりに使用
        time: event.totalTime,
        timeComparison: event.totalTimeComparison,
    }));
}

/**
 * 比率データの前期データを棒グラフ用データに変換（TransitionEventItems形式）
 * @param ratioData 比率データ
 * @returns 前期データの棒グラフ用データ（TransitionEventItems形式）
 */
export function convertRatioPreviousToBarChartData(ratioData: RatioEvents): TransitionEventItems[] {
    if (!ratioData.previousAggregatedDataByEventList) {
        return [];
    }

    return ratioData.previousAggregatedDataByEventList.map((event) => ({
        date: event.title, // イベント名を日付の代わりに使用
        time: event.totalTime,
    }));
}

/**
 * Transitionデータのランキング比較を順位ベースで計算
 * @param currentData 今期データ
 * @param previousData 前期データ
 * @returns ランキング用データ（順位ベース比較）
 */
export function calculateTransitionRanking(
    currentData: TransitionEventItems[],
    previousData?: TransitionEventItems[],
) {
    // 今期データを時間順でソート（上位3つ）
    const currentRanking = [...currentData].sort((a, b) => b.time - a.time).slice(0, 3);

    // 前期データがある場合は前期ランキングも作成
    let previousRanking: TransitionEventItems[] = [];
    if (previousData && previousData.length > 0) {
        previousRanking = [...previousData].sort((a, b) => b.time - a.time).slice(0, 3);
    }

    // 順位ベースで比較（1位vs1位、2位vs2位、3位vs3位）
    return currentRanking.map((currentItem, index) => {
        const previousItem = previousRanking[index]; // 同じ順位の前期データ

        return {
            title: currentItem.date.slice(5), // 先頭5文字（"YYYY/"）を削除
            percentage: 0, // percentageは扱わないので0で設定
            totalTime: currentItem.time,
            timeComparison: previousItem
                ? calculatePeriodComparison(currentItem.time, previousItem.time)
                : undefined, // 前期データがない場合は比較なし
        };
    });
}
